import { Injectable, computed, inject } from '@angular/core';
import { Observable, of } from 'rxjs';
import { CompanySessionService } from '@core/auth/company-session.service';
import { CompanyProfileSetupService } from '@core/company/company-profile-setup.service';
import type { OnboardingStep, OnboardingStepId, OnboardingStepStatus } from './onboarding.types';
import { buildSteps, currentStepIdOf, percentOf, statusOf } from './onboarding-progress.util';

/**
 * Derives the recruitment getting-started checklist. Every step —
 * `account-manager`, `first-hire-request` and `company-profile` — reads off the
 * already-loaded company details, so the checklist needs no fetching of its own
 * and tracks the session signal automatically.
 */
@Injectable({ providedIn: 'root' })
export class OnboardingProgressService {
  private readonly session = inject(CompanySessionService);
  private readonly profileSetup = inject(CompanyProfileSetupService);

  /** Every step with its current completion state, in display order. */
  readonly steps = computed<OnboardingStep[]>(() => {
    const company = this.session.activeCompanyDetails();
    return buildSteps({
      'first-hire-request': (company?.totalJobPosts ?? 0) > 0,
      // Same rule the company-profile nudge banners gate on.
      'company-profile': this.profileSetup.isComplete(),
      'account-manager': !!company?.responsiblePerson?.name,
    });
  });

  /** Whole-number completion percentage across all steps. */
  readonly percentComplete = computed(() => percentOf(this.steps()));

  /** True once every step is complete. */
  readonly isComplete = computed(() => this.percentComplete() === 100);

  /** Id of the first incomplete step (the "current" node), or null when done. */
  readonly currentStepId = computed<OnboardingStepId | null>(() => currentStepIdOf(this.steps()));

  /** Visual state for a given step relative to the current step. */
  statusOf(step: OnboardingStep): OnboardingStepStatus {
    return statusOf(this.steps(), step);
  }

  /**
   * Emit the current completion state. Kept observable-shaped for the routing
   * guard and shell, which resolve onboarding before deciding where to send the
   * user; completion is now derived synchronously, so this never fetches.
   */
  ensureLoaded(): Observable<boolean> {
    return of(this.isComplete());
  }
}
